import { useEffect, useState, useCallback } from "react";
import { pb } from "./pocketbase";
import {
  IntegrationsContext,
  IntegrationsContextType,
} from "@/contexts/integrations-context";
import { useAuth } from "@/contexts/auth-context";
import { useBrand } from "@/contexts/brand-context";
import { Integrations } from "@/types/pocketbase-types";

export const IntegrationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isAuthenticated } = useAuth();
  const { currentBrand } = useBrand();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [integrations, setIntegrations] = useState<Integrations[]>([]);

  // Fetch integrations data - wrapped in useCallback to avoid recreation on every render
  const fetchIntegrations = useCallback(async () => {
    if (!isAuthenticated || !currentBrand?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const records = await pb
        .collection("integrations")
        .getList<Integrations>(1, 50, {
          filter: `brand = '${currentBrand.id}' && status = 'active'`,
          sort: "-created",
        });

      setIntegrations(records.items);
    } catch (err) {
      console.error("Error fetching integrations:", err);
      setError(
        err instanceof Error ? err : new Error("Failed to fetch integrations"),
      );
      setIntegrations([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, currentBrand?.id]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (isAuthenticated && currentBrand?.id) {
      // Fetch initial data
      fetchIntegrations();

      // Subscribe to realtime updates
      const subscribeToChanges = async () => {
        try {
          console.log(
            "Setting up realtime subscription for integrations collection",
          );

          await pb.collection("integrations").subscribe("*", (e) => {
            console.log("Integration change detected:", e.action, e.record);

            // Only handle changes for the current brand
            if (e.record.brand !== currentBrand.id) {
              return;
            }

            if (e.action === "create") {
              console.log("Adding new integration:", e.record);
              setIntegrations((prev) => [
                e.record as unknown as Integrations,
                ...prev,
              ]);
            } else if (e.action === "update") {
              console.log("Updating integration:", e.record);
              setIntegrations((prev) =>
                prev.map((integration) =>
                  integration.id === e.record.id
                    ? (e.record as unknown as Integrations)
                    : integration,
                ),
              );
            } else if (e.action === "delete") {
              console.log("Removing integration:", e.record);
              setIntegrations((prev) =>
                prev.filter((integration) => integration.id !== e.record.id),
              );
            }
          });
        } catch (err) {
          console.error("Error setting up integrations listener:", err);
        }
      };

      subscribeToChanges();

      // Cleanup on unmount
      return () => {
        pb.collection("integrations").unsubscribe("*");
      };
    } else {
      setIntegrations([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, currentBrand?.id, fetchIntegrations]);

  // Helper function to get integrations by category
  const getIntegrationsByCategory = useCallback(
    (category: string) => {
      return integrations.filter(
        (integration) => integration.category === category,
      );
    },
    [integrations],
  );

  // Helper function to get integrations by type
  const getIntegrationsByType = useCallback(
    (type: string) => {
      return integrations.filter((integration) => integration.type === type);
    },
    [integrations],
  );

  // Helper function to get active integrations
  const getActiveIntegrations = useCallback(() => {
    return integrations.filter(
      (integration) => integration.status === "active",
    );
  }, [integrations]);

  const contextValue: IntegrationsContextType = {
    isLoading,
    error,
    integrations,
    refreshIntegrations: fetchIntegrations,
    getIntegrationsByCategory,
    getIntegrationsByType,
    getActiveIntegrations,
  };

  return (
    <IntegrationsContext.Provider value={contextValue}>
      {children}
    </IntegrationsContext.Provider>
  );
};
